/**
 * Ruimte-detectie voor het drieluik (feedback 2026-09-27): op een cycle- of
 * composite-tab tonen béíde flankerende planeetgrids twee kolommen (D + P) en
 * op Full HD schuiven de buitenranden van de zijcharts (milt-hoek links,
 * solar-plexus-hoek rechts) dan onder die grids. De zijcharts schuiven in dat
 * geval net genoeg naar het midden voor een kleine tussenruimte; aan de
 * binnenkant is daar plaats voor omdat het middenchart lager staat. Zonder
 * overlap (gewone tab met enkelkoloms grids, QHD) blijft de inschuif 0 en
 * verandert er niets. De pagina meet de geometrie; deze helper beslist.
 */

/** Afmetingen van het bodygraph-asset (`bodygraph-full.svg`, viewBox-eenheden). */
export const HD_BODYGRAPH_VIEWBOX_WIDTH = 1182;
export const HD_BODYGRAPH_VIEWBOX_HEIGHT = 1772;

/**
 * Horizontale grenzen van de getekende inhoud binnen die viewBox: de
 * buitenhoek van de milt links en die van de solar plexus rechts (gemeten op
 * het asset). Daarbuiten is de svg leeg.
 */
export const HD_BODYGRAPH_CONTENT_LEFT = 80;
export const HD_BODYGRAPH_CONTENT_RIGHT = 1109;

/** Gewenste ademruimte (px) tussen een planeetgrid en de zichtbare zijchart. */
export const HD_TRIPLE_GRID_GAP_PX = 12;

/**
 * Plafond van de inschuif, als fractie van de getekende zijchartbreedte. Het
 * dichtste punt tussen zijchart en middenchart (solar-plexus-hoek links ↔
 * kanaal keel→milt van het middenchart) krimpt met ±0,85 px per px inschuif;
 * op Full HD (zijchart ±335px) laat 0,18 nog ±30px tussen beide over. Wordt
 * het plafond bereikt, dan blijft de rest-overlap met de grids staan i.p.v.
 * dat de charts in elkaar schuiven.
 */
export const HD_TRIPLE_MAX_PULL_RATIO = 0.18;

/** Box van een zijchart-element (viewport-px), zoals `getBoundingClientRect`. */
export interface HdTripleChartBox {
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/** Eén meting; alle x-waarden in viewport-px, in de huidige (verschoven) stand. */
export interface HdTripleChartSpaceMeasurement {
  /** Rechterrand van het linker planeetgrid, null zonder zichtbaar grid. */
  readonly leftGridRight: number | null;
  /** Linkerrand van het rechter planeetgrid, null zonder zichtbaar grid. */
  readonly rightGridLeft: number | null;
  /** Box van de linker zijchart (de bodygraph-component). */
  readonly leftChart: HdTripleChartBox;
  /** Box van de rechter zijchart (de bodygraph-component). */
  readonly rightChart: HdTripleChartBox;
  /** Inschuif (px) die tijdens de meting al toegepast was. */
  readonly appliedPull: number;
}

/** Zichtbare linker-/rechterrand en getekende breedte van een bodygraph in zijn box. */
export function bodygraphContentEdges(box: HdTripleChartBox): {
  readonly left: number;
  readonly right: number;
  readonly drawnWidth: number;
} {
  // De svg vult de box hoogte-gedreven (breedte begrensd door de box) en
  // tekent `xMidYMid meet`: horizontaal gecentreerd, schaal = de krapste as.
  const scale = Math.min(box.height / HD_BODYGRAPH_VIEWBOX_HEIGHT, box.width / HD_BODYGRAPH_VIEWBOX_WIDTH);
  const drawnWidth = HD_BODYGRAPH_VIEWBOX_WIDTH * scale;
  const drawnLeft = box.left + (box.width - drawnWidth) / 2;
  return {
    left: drawnLeft + HD_BODYGRAPH_CONTENT_LEFT * scale,
    right: drawnLeft + HD_BODYGRAPH_CONTENT_RIGHT * scale,
    drawnWidth,
  };
}

/**
 * Inschuif (hele px, ≥ 0) per zijchart richting het midden. De inschuif is
 * symmetrisch: het drieluik blijft gecentreerd en elke buitenrand schuift
 * precies evenveel naar binnen. De krapste kant beslist. De meting gebeurt in
 * de al verschoven stand, dus eerst terugrekenen naar de natuurlijke stand;
 * zo convergeert het resultaat meteen en ontstaat er geen meet-lus.
 */
export function tripleChartSidePullPx(measurement: HdTripleChartSpaceMeasurement): number {
  const left = bodygraphContentEdges(measurement.leftChart);
  const right = bodygraphContentEdges(measurement.rightChart);
  const applied = Math.max(0, measurement.appliedPull);
  const shortfalls: number[] = [];
  if (measurement.leftGridRight != null) {
    const naturalLeft = left.left - applied;
    shortfalls.push(measurement.leftGridRight + HD_TRIPLE_GRID_GAP_PX - naturalLeft);
  }
  if (measurement.rightGridLeft != null) {
    const naturalRight = right.right + applied;
    shortfalls.push(naturalRight - (measurement.rightGridLeft - HD_TRIPLE_GRID_GAP_PX));
  }
  const needed = Math.max(0, ...shortfalls);
  if (needed <= 0) {
    return 0;
  }
  const cap = HD_TRIPLE_MAX_PULL_RATIO * Math.min(left.drawnWidth, right.drawnWidth);
  return Math.ceil(Math.min(needed, cap));
}
